import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { DigitalSignStatusEnum } from "domain/enum/digitalSignStatus";
import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { DigitalSignInputFilter } from "domain/models/digital-sign-input-filter";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import { useDigitalSignByFilter } from "presentation/hooks/use-query-digital-sign";
import { useCreateDigitalSignApproverMutation } from "presentation/hooks/use-query-digital-sign-approver";
import {
  useTripsheetsByFilter,
  useUpdateTripsheetMutation,
} from "presentation/hooks/use-query-tripsheet";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFilterDashboardStore } from "presentation/stores/use-filter-dashboard-store";
import { Button } from "primereact/button";
import { DataTable, DataTableValueArray } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useCallback, useRef, useState } from "react";

const RETRY_CONFIG = {
  MAX_RETRIES: 2,
  BASE_DELAY: 300,
  DOCUMENT_DELAY: 100,
} as const;

const UI_CONFIG = {
  TOAST_LIFE: 4000,
  BUTTON_MIN_WIDTH: "200px",
  SYNC_INDICATOR_STYLE: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "#e3f2fd",
    borderRadius: "6px",
    border: "1px solid #bbdefb",
  },
} as const;

interface ParamProps {
  level: number;
  tasks: DataTableValueArray;
  refresh(): void;
  dt: React.RefObject<DataTable<DataTableValueArray> | null>;
}

const HeaderDashboardToApprovalGrid: React.FC<ParamProps> = (
  params: ParamProps
) => {
  const toast = useRef<Toast>(null);
  const { setFilters, resetFilters } = useFilterDashboardStore();
  const { userEmployee, email: userEmployeeEmail } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    data: tripsheetsData,
    isLoading: isLoadingTripsheets,
    isFetching: isFetchingTripsheets,
  } = useTripsheetsByFilter({
    status: TripsheetStatusEnum.A_ASSINAR,
    limit: 1000,
  } as TripsheetInputFilter);

  const { mutateAsync: updateTripsheet } = useUpdateTripsheetMutation();
  const { mutateAsync: createDigitalSingApprover } =
    useCreateDigitalSignApproverMutation();

  const {
    data: digitalSignData,
    isLoading: isLoadingDigitalSigns,
    isFetching: isFetchingDigitalSigns,
  } = useDigitalSignByFilter({
    status: DigitalSignStatusEnum.A_ASSINAR,
    limit: 1000,
  } as DigitalSignInputFilter);
  const clearFilter = useCallback(() => {
    setFilters({});
    params.dt.current?.reset();
    resetFilters();
    setFilters({
      query: { value: "", matchMode: "contains" },
      type: { value: "", matchMode: "contains" },
      description: { value: "", matchMode: "contains" },
    });
  }, [setFilters, resetFilters, params.dt]);

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    toast.current?.show({
      severity: type,
      summary: subject,
      detail: message,
      life: UI_CONFIG.TOAST_LIFE,
    });
  };

  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxRetries: number = RETRY_CONFIG.MAX_RETRIES,
    baseDelay: number = RETRY_CONFIG.BASE_DELAY
  ): Promise<T> => {
    let lastError: Error = new Error("Operação não executada");

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries) {
          throw lastError;
        }

        const delay = baseDelay * (attempt + 1);
        console.warn(
          `Tentativa ${attempt + 1} falhou, tentando novamente em ${delay}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError || new Error("Operação falhou após múltiplas tentativas");
  };

  const processDocumentWithRetry = async (
    item: any
  ): Promise<{ success: boolean; documentId: string; error?: any }> => {
    const documentId = item?.id ?? "";

    console.log(
      `🔧 [PROCESSO] Iniciando processamento do documento ${documentId}`,
      { item }
    );

    try {
      console.log(
        `🔍 [PROCESSO] Validando entrada para documento ${documentId}`
      );

      if (!item || typeof item !== "object") {
        console.error(
          `❌ [PROCESSO] Item inválido para documento ${documentId}:`,
          item
        );
        throw new Error("Item de documento inválido");
      }

      if (!documentId || documentId.trim() === "") {
        console.error(`❌ [PROCESSO] ID não fornecido:`, { documentId, item });
        throw new Error("ID do documento não fornecido");
      }

      console.log(
        `🔍 [PROCESSO] Buscando tripsheet para documento ${documentId}`
      );
      const tripsheet = tripsheetsData?.data?.find(
        (tsItem) => tsItem.id === documentId
      );

      console.log(`📋 [PROCESSO] Tripsheet encontrado:`, {
        documentId,
        tripsheet,
        found: !!tripsheet,
      });

      if (!tripsheet) {
        console.error(
          `❌ [PROCESSO] Tripsheet não encontrado para ${documentId}`
        );
        throw new Error(`Tripsheet ${documentId} não encontrado`);
      }

      console.log(
        `✅ [PROCESSO] Validando status do tripsheet ${documentId}:`,
        {
          currentStatus: tripsheet.status,
          expectedStatus: TripsheetStatusEnum.A_ASSINAR,
          isValid: tripsheet.status === TripsheetStatusEnum.A_ASSINAR,
        }
      );

      if (tripsheet.status !== TripsheetStatusEnum.A_ASSINAR) {
        console.error(`❌ [PROCESSO] Status inválido para ${documentId}:`, {
          currentStatus: tripsheet.status,
          expectedStatus: TripsheetStatusEnum.A_ASSINAR,
        });
        throw new Error(
          `Status inválido: ${tripsheet.status}. Esperado: ${TripsheetStatusEnum.A_ASSINAR}`
        );
      }

      console.log(
        `🔍 [PROCESSO] Buscando assinatura digital para documento ${documentId}`
      );
      const digitalSign = digitalSignData?.data?.find(
        (docToSign) => docToSign.referenceId === documentId
      );

      console.log(`🔏 [PROCESSO] Assinatura digital encontrada:`, {
        documentId,
        digitalSign,
        found: !!digitalSign,
      });

      if (!digitalSign) {
        console.error(
          `❌ [PROCESSO] Assinatura digital não encontrada para ${documentId}`
        );
        throw new Error(
          `Assinatura digital não encontrada para documento ${documentId}`
        );
      }

      console.log(
        `✅ [PROCESSO] Validando status da assinatura digital ${documentId}:`,
        {
          currentStatus: digitalSign.status,
          expectedStatus: DigitalSignStatusEnum.A_ASSINAR,
          isValid: digitalSign.status === DigitalSignStatusEnum.A_ASSINAR,
        }
      );

      if (digitalSign.status !== DigitalSignStatusEnum.A_ASSINAR) {
        console.error(
          `❌ [PROCESSO] Status da assinatura digital inválido para ${documentId}:`,
          {
            currentStatus: digitalSign.status,
            expectedStatus: DigitalSignStatusEnum.A_ASSINAR,
          }
        );
        throw new Error(
          `Status da assinatura digital inválido: ${digitalSign.status}`
        );
      }

      if (!userEmployee?.id || !userEmployeeEmail) {
        throw new Error("Usuário não possui credenciais válidas");
      }

      const signApproval: DigitalSignApprover = {
        employee: {
          id: userEmployee.id,
        },
        digitalSign: digitalSign,
        step: DigitalSignStepEnum.STEP_COMPANY,
      };

      const updatedTripsheet = {
        ...tripsheet,
        status: TripsheetStatusEnum.ASSINADO_1,
        lastModifiedBy: userEmployeeEmail,
      };

      console.log(`🔄 Processando documento ${documentId}...`);

      const [signResult, updateResult] = await Promise.allSettled([
        retryWithBackoff(async () => {
          await createDigitalSingApprover(signApproval);
        }, RETRY_CONFIG.MAX_RETRIES),
        retryWithBackoff(async () => {
          await updateTripsheet(updatedTripsheet);
        }, RETRY_CONFIG.MAX_RETRIES),
      ]);

      // Verificar se ambas operações foram bem-sucedidas
      if (signResult.status === "rejected") {
        throw new Error(`Falha na assinatura: ${signResult.reason}`);
      }
      if (updateResult.status === "rejected") {
        throw new Error(`Falha na atualização: ${updateResult.reason}`);
      }

      console.log(`✅ Documento ${documentId} processado com sucesso`);
      return { success: true, documentId };
    } catch (error) {
      console.error(`❌ Erro ao processar documento ${documentId}:`, error);
      return { success: false, documentId, error };
    }
  };

  const aprovarSelectedTasks = async () => {
    console.log("🚀 [APROVAÇÃO] Iniciando fluxo de aprovação selecionados");
    console.log("📋 [APROVAÇÃO] Documentos selecionados:", params.tasks);
    console.log("📊 [APROVAÇÃO] Total de documentos:", params.tasks.length);

    // Log detalhado dos itens selecionados
    console.log("🔍 [APROVAÇÃO] Detalhes dos itens selecionados:");
    params.tasks.forEach((item: any, index: number) => {
      console.log(`📄 [APROVAÇÃO] Item ${index + 1}:`, {
        id: item?.id,
        type: item?.type,
        description: item?.description,
        status: item?.status,
        employeeId: item?.employeeId,
        dateRef: item?.dateRef,
        fullItem: item,
      });
    });

    // Tabela formatada para melhor visualização
    if (params.tasks.length > 0) {
      console.table(
        params.tasks.map((item: any, index: number) => ({
          "#": index + 1,
          ID: item?.id || "N/A",
          Tipo: item?.type || "N/A",
          Descrição: item?.description || "N/A",
          Status: item?.status || "N/A",
          "Employee ID": item?.employeeId || "N/A",
        }))
      );
    }

    if (params.tasks.length <= 0) {
      console.warn("⚠️ [APROVAÇÃO] Nenhum documento selecionado - abortando");
      showMessage(
        MessageTypeEnum.WARN,
        "Workflow",
        "Nenhum documento selecionado"
      );
      return;
    }

    console.log("👤 [APROVAÇÃO] Validando usuário:", {
      userEmployeeId: userEmployee?.id,
      userEmployeeEmail,
      isValid: !!(userEmployee?.id && userEmployeeEmail),
    });

    if (!userEmployee?.id || !userEmployeeEmail) {
      console.error("❌ [APROVAÇÃO] Usuário não autenticado - abortando");
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Usuário não autenticado corretamente"
      );
      return;
    }

    // Verificar se dados ainda estão carregando
    console.log("📊 [APROVAÇÃO] Status de carregamento:", {
      isLoadingTripsheets,
      isLoadingDigitalSigns,
      isStillLoading: isLoadingTripsheets || isLoadingDigitalSigns,
    });

    if (isLoadingTripsheets || isLoadingDigitalSigns) {
      console.warn("⏳ [APROVAÇÃO] Dados ainda carregando - aguardando");
      showMessage(
        MessageTypeEnum.INFO,
        "Workflow",
        "⏳ Carregando dados necessários. Aguarde..."
      );
      return;
    }

    console.log("📋 [APROVAÇÃO] Dados disponíveis:", {
      tripsheetsCount: tripsheetsData?.data?.length || 0,
      digitalSignsCount: digitalSignData?.data?.length || 0,
      tripsheetsData: tripsheetsData?.data,
      digitalSignData: digitalSignData?.data,
    });

    if (!tripsheetsData?.data || tripsheetsData.data.length === 0) {
      console.error("❌ [APROVAÇÃO] Nenhum tripsheet disponível - abortando");
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "❌ Nenhum tripsheet disponível para aprovação. Recarregue a página."
      );
      return;
    }

    if (!digitalSignData?.data || digitalSignData.data.length === 0) {
      console.error(
        "❌ [APROVAÇÃO] Nenhuma assinatura digital encontrada - abortando"
      );
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "❌ Nenhuma assinatura digital encontrada. Recarregue a página."
      );
      return;
    }

    // Iniciar processamento robusto
    console.log("⚙️ [APROVAÇÃO] Iniciando processamento robusto");
    console.log("🔧 [APROVAÇÃO] Configurações:", { RETRY_CONFIG, UI_CONFIG });

    setIsProcessing(true);
    const totalDocuments = params.tasks.length;
    let processedCount = 0;
    const failedDocuments: { id: string; error: any }[] = [];

    console.log("📊 [APROVAÇÃO] Variáveis de controle inicializadas:", {
      totalDocuments,
      processedCount,
      failedDocuments: failedDocuments.length,
    });

    try {
      console.log("🚀 [APROVAÇÃO] Enviando mensagem de início para usuário");
      showMessage(
        MessageTypeEnum.INFO,
        "Workflow",
        `🚀 Iniciando processamento paralelo de ${totalDocuments} documento${
          totalDocuments > 1 ? "s" : ""
        }...`
      );

      // Processar documentos em paralelo para melhor performance
      console.log(
        "⚡ [APROVAÇÃO] Criando promises de processamento para execução paralela"
      );
      const processingPromises = params.tasks.map((item, index) => {
        console.log(
          `📄 [APROVAÇÃO] Documento ${index + 1}/${totalDocuments}:`,
          { id: item?.id, type: item?.type }
        );
        return processDocumentWithRetry(item);
      });

      console.log(
        "⏳ [APROVAÇÃO] Executando processamento paralelo - aguardando resultados..."
      );
      const startTime = Date.now();
      const results = await Promise.allSettled(processingPromises);
      const endTime = Date.now();

      console.log(
        `⚡ [APROVAÇÃO] Processamento paralelo concluído em ${
          endTime - startTime
        }ms`
      );
      console.log("📊 [APROVAÇÃO] Resultados brutos:", results);

      results.forEach((result, index) => {
        processedCount++;
        const documentId = params.tasks[index]?.id || `doc-${index}`;

        console.log(`📋 [APROVAÇÃO] Processando resultado ${index + 1}:`, {
          documentId,
          status: result.status,
          success: result.status === "fulfilled" ? result.value.success : false,
        });

        if (result.status === "fulfilled" && !result.value.success) {
          console.error(
            `❌ [APROVAÇÃO] Documento ${documentId} falhou (fulfilled mas não success):`,
            result.value.error
          );
          failedDocuments.push({
            id: result.value.documentId,
            error: result.value.error,
          });
        } else if (result.status === "rejected") {
          console.error(
            `❌ [APROVAÇÃO] Documento ${documentId} rejeitado:`,
            result.reason
          );
          failedDocuments.push({ id: documentId, error: result.reason });
        } else if (result.status === "fulfilled" && result.value.success) {
          console.log(
            `✅ [APROVAÇÃO] Documento ${documentId} processado com sucesso`
          );
        }
      });

      console.log(`📊 [APROVAÇÃO] Resumo do processamento:`, {
        totalDocuments,
        processedCount,
        successfulCount: processedCount - failedDocuments.length,
        failedCount: failedDocuments.length,
        failedDocuments,
      });

      // Resultados finais
      const successfulCount = processedCount - failedDocuments.length;

      console.log("🎯 [APROVAÇÃO] Analisando resultados finais:", {
        successfulCount,
        failedCount: failedDocuments.length,
        totalProcessed: processedCount,
        isCompleteSuccess: failedDocuments.length === 0,
        isCompleteFailure: successfulCount === 0,
        isPartialSuccess: successfulCount > 0 && failedDocuments.length > 0,
      });

      if (failedDocuments.length === 0) {
        console.log(
          "🎉 [APROVAÇÃO] SUCESSO COMPLETO - todos documentos aprovados"
        );
        showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          `🎉 SUCESSO! Todos os ${successfulCount} documentos foram aprovados!`
        );
      } else if (successfulCount === 0) {
        console.error("💥 [APROVAÇÃO] FALHA TOTAL - nenhum documento aprovado");
        console.error("📋 [APROVAÇÃO] Detalhes dos erros:", failedDocuments);
        showMessage(
          MessageTypeEnum.ERROR,
          "Workflow",
          `❌ FALHA TOTAL! Nenhum dos ${failedDocuments.length} documentos foi processado. Verifique os logs.`
        );
      } else {
        console.warn(
          "⚠️ [APROVAÇÃO] SUCESSO PARCIAL - alguns documentos falharam"
        );
        console.error(
          "📋 [APROVAÇÃO] Documentos que falharam:",
          failedDocuments
        );
        showMessage(
          MessageTypeEnum.WARN,
          "Workflow",
          `⚠️ PARCIAL: ${successfulCount} aprovados, ${failedDocuments.length} falharam. Verifique os logs.`
        );
      }

      // Sempre atualizar o grid após processamento
      console.log("🔄 [APROVAÇÃO] Iniciando atualização do grid...");
      const refreshStartTime = Date.now();
      await params.refresh();
      const refreshEndTime = Date.now();
      console.log(
        `✅ [APROVAÇÃO] Grid atualizado em ${
          refreshEndTime - refreshStartTime
        }ms`
      );
    } catch (criticalError) {
      console.error(
        "💥 [APROVAÇÃO] ERRO CRÍTICO durante o processamento:",
        criticalError
      );
      console.error(
        "🔍 [APROVAÇÃO] Stack trace:",
        (criticalError as Error)?.stack
      );
      console.error("📊 [APROVAÇÃO] Estado no momento do erro:", {
        totalDocuments,
        processedCount,
        failedDocuments: failedDocuments.length,
        isProcessing,
      });
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "❌ Erro crítico! Operação interrompida. Contate o suporte técnico."
      );
    } finally {
      console.log("🏁 [APROVAÇÃO] Finalizando processamento...");
      setIsProcessing(false);
      console.log("✅ [APROVAÇÃO] Processamento completamente finalizado");
      console.log("📊 [APROVAÇÃO] Estado final:", {
        isProcessing: false,
        totalDocumentsProcessed: processedCount,
        finalFailedCount: failedDocuments.length,
      });
    }
  };

  return (
    <div className="flex justify-content-between">
      <div>
        {/* Indicador de atualização em background */}
        {(isFetchingTripsheets || isFetchingDigitalSigns) && (
          <div style={UI_CONFIG.SYNC_INDICATOR_STYLE}>
            <i
              className="pi pi-spin pi-sync"
              style={{ color: "#1976d2", fontSize: "0.9rem" }}
            />
            <span
              style={{
                color: "#1976d2",
                fontSize: "0.85rem",
                fontWeight: "500",
              }}
            >
              Sincronizando dados...
            </span>
          </div>
        )}
      </div>
      <div style={{ display: "flex" }}>
        {params.level === 2 && (
          <Button
            type="button"
            icon={
              isProcessing || isLoadingTripsheets || isLoadingDigitalSigns
                ? "pi pi-spin pi-spinner"
                : "pi pi-check"
            }
            label={
              isProcessing
                ? "Processando..."
                : isLoadingTripsheets || isLoadingDigitalSigns
                ? "Carregando dados..."
                : "Assinar Selecionados"
            }
            outlined
            onClick={aprovarSelectedTasks}
            disabled={
              isProcessing ||
              params.tasks.length === 0 ||
              isLoadingTripsheets ||
              isLoadingDigitalSigns
            }
            loading={
              isProcessing || isLoadingTripsheets || isLoadingDigitalSigns
            }
            style={{
              backgroundColor:
                isProcessing || isLoadingTripsheets || isLoadingDigitalSigns
                  ? "#95a5a6"
                  : "#2196f3",
              color: "white",
              borderRadius: "8px",
              minWidth: UI_CONFIG.BUTTON_MIN_WIDTH,
            }}
          />
        )}
        <div style={{ width: "10px" }}></div>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Limpar"
          outlined
          onClick={clearFilter}
          style={{
            backgroundColor: "white",
            borderColor: "#2196f3",
            color: "#2196f3",
            borderRadius: "8px",
          }}
        />
      </div>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};
export default HeaderDashboardToApprovalGrid;
